import React, { useEffect, useState } from "react";
import PageTableLayout from "../../components/Pagetablelayout";
import axios from "axios";
import { Space, Button, Modal, Form, Input, Select, InputNumber, Switch, Tag } from "antd";

const STAY_TYPES = ["Hotel", "Resort", "Lodge", "Villa", "Guesthouse"];

export default function StaysPage() {
  const [stays, setStays] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStay, setEditingStay] = useState(null);
  const [form] = Form.useForm();

  const token = localStorage.getItem("jwt"); // matches AdminLogin storage key
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchStays = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://mdgholidaydreams.com/api/stays", {
        headers: authHeaders,
      });
      setStays(res.data);
    } catch (err) {
      console.error("Fetch stays failed:", err);
    }
    setLoading(false);
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get("https://mdgholidaydreams.com/api/cities", {
        headers: authHeaders,
      });
      setCities(res.data);
    } catch (err) {
      console.error("Fetch cities failed:", err);
    }
  };

  useEffect(() => {
    fetchStays();
    fetchCities();
  }, []);

  const handleAdd = () => {
    setEditingStay(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingStay(record);
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      stars: record.stars,
      price: record.price,
      image: record.image,
      description: record.description,
      city_id: record.city_id,
      featured: !!record.featured,
      active: !!record.active,
      amenities: Array.isArray(record.amenities) ? record.amenities : [],
    });
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        active: values.active ? 1 : 0,
        featured: values.featured ? 1 : 0,
        amenities: values.amenities || [],
      };

      if (editingStay) {
        await axios.put(
          `https://mdgholidaydreams.com/api/stays?id=${editingStay.id}`,
          payload,
          { headers: authHeaders }
        );
      } else {
        await axios.post("https://mdgholidaydreams.com/api/stays", payload, {
          headers: authHeaders,
        });
      }

      form.resetFields();
      setIsModalVisible(false);
      setEditingStay(null);
      fetchStays();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingStay(null);
    form.resetFields();
  };

  const handleToggleActive = (record) => {
    axios
      .patch(
        `https://mdgholidaydreams.com/api/stays?id=${record.id}`,
        {},
        { headers: authHeaders }
      )
      .then(fetchStays)
      .catch((err) => console.error("Toggle active failed:", err));
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete "${record.name}"?`,
      content: "This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: () =>
        axios
          .delete(`https://mdgholidaydreams.com/api/stays?id=${record.id}`, {
            headers: authHeaders,
          })
          .then(fetchStays)
          .catch((err) => console.error("Delete failed:", err)),
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Type", dataIndex: "type", key: "type" },
    {
      title: "Stars",
      dataIndex: "stars",
      key: "stars",
      render: (s) => (s ? "★".repeat(s) : "—"),
    },
    {
      title: "Price/night",
      dataIndex: "price",
      key: "price",
      render: (p) => (p != null ? `$${Number(p).toLocaleString()}` : "—"),
    },
    { title: "City", dataIndex: "city_name", key: "city_name" },
    { title: "Country", dataIndex: "country_name", key: "country_name" },
    {
      title: "Featured",
      dataIndex: "featured",
      key: "featured",
      render: (f) => (f ? <Tag color="gold">Featured</Tag> : "—"),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active) =>
        active ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    { title: "Created By", dataIndex: "created_by", key: "created_by" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)}>Edit</Button>
          <Button onClick={() => handleToggleActive(record)}>
            {record.active ? "Deactivate" : "Activate"}
          </Button>
          <Button danger onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTableLayout
        breadcrumb={["Admin", "Stays"]}
        title="Manage Stays"
        addButtonLabel="Add Stay"
        onAdd={handleAdd}
        columns={columns}
        dataSource={stays}
        loading={loading}
        onRefresh={fetchStays}
      />

      <Modal
        title={editingStay ? "Edit Stay" : "Add New Stay"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save"
        width={600}
      >
        <Form form={form} layout="vertical" initialValues={{ active: true, featured: false }}>
          <Form.Item
            name="name"
            label="Stay Name"
            rules={[{ required: true, message: "Please enter a stay name" }]}
          >
            <Input />
          </Form.Item>

          <Space style={{ display: "flex" }} align="start">
            <Form.Item name="type" label="Type" style={{ width: 220 }}>
              <Select placeholder="Select type" allowClear>
                {STAY_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>
                    {t}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="stars" label="Stars" style={{ width: 140 }}>
              <Select placeholder="Stars" allowClear>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Select.Option key={n} value={n}>
                    {"★".repeat(n)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="price"
              label="Price / night (USD)"
              rules={[{ required: true, message: "Please enter a price" }]}
              style={{ width: 200 }}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="city_id"
            label="City"
            rules={[{ required: true, message: "Please select a city" }]}
          >
            <Select
              placeholder="Select city"
              showSearch
              optionFilterProp="children"
            >
              {cities.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name} {c.country_name ? `(${c.country_name})` : ""}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="image" label="Image URL">
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name="amenities" label="Amenities">
            <Select mode="tags" placeholder="Pool, WiFi, Spa...">
            </Select>
          </Form.Item>

          <Space style={{ display: "flex" }}>
            <Form.Item name="featured" label="Featured" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </>
  );
}